"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useConsent } from "@/components/lgpd/consent-store";
import { getOrCreateSessionId, shouldTrack } from "@/lib/analytics-client";

interface ClickEventData {
  x: number;
  y: number;
  scrollDepth: number;
}

const MAX_QUEUE = 50;
const FLUSH_INTERVAL = 30_000;

export function HeatmapTracker() {
  const pathname = usePathname();
  const { preferences, hydrated } = useConsent();
  const queue = useRef<ClickEventData[]>([]);
  const maxScroll = useRef(0);
  const active = shouldTrack(preferences.analytics) && hydrated;

  function flush(path: string) {
    if (queue.current.length === 0) return;
    const events = queue.current.splice(0);
    const payload = JSON.stringify({ path, sessionId: getOrCreateSessionId(), events });
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/analytics/click", new Blob([payload], { type: "application/json" }));
    } else {
      fetch("/api/analytics/click", { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
    }
  }

  useEffect(() => {
    if (!active) return;

    maxScroll.current = 0;

    function onClick(e: MouseEvent) {
      if (queue.current.length >= MAX_QUEUE) return;
      const docHeight = Math.max(document.body.scrollHeight, window.innerHeight);
      queue.current.push({
        x: Math.round((e.clientX / window.innerWidth) * 100),
        y: Math.round(((e.clientY + window.scrollY) / docHeight) * 100),
        scrollDepth: maxScroll.current,
      });
    }

    function onScroll() {
      const docHeight = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      const pct = Math.round((window.scrollY / docHeight) * 100);
      if (pct > maxScroll.current) maxScroll.current = pct;
    }

    function onHidden() {
      if (document.visibilityState === "hidden") flush(pathname);
    }

    document.addEventListener("click", onClick);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onHidden);
    const timer = setInterval(() => flush(pathname), FLUSH_INTERVAL);

    return () => {
      flush(pathname);
      document.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onHidden);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, active]);

  return null;
}
